import { useTheme, useMediaQuery } from "@mui/material";
import React from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Area,
  ResponsiveContainer,
  ComposedChart,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import { useSelector } from "react-redux";


export default function BPChart({ data, systolicProjection, diastolicProjection }) {

  const patientReducer = useSelector((state) => state.patientReducer);
  const patientType = patientReducer?.patientInfo?.patientType || 'OBSTETRIC'

  const buildProjectionOnlyData = () => {
    const startDate = dayjs();

    return systolicProjection.map((sys, index) => ({
      fullDate: startDate.add(index * 7, "day").valueOf(),
      week: sys.week,
      sysRange: [sys.min, sys.max],
      diaRange: [
        diastolicProjection[index]?.min,
        diastolicProjection[index]?.max,
      ],
    }));
  };

  const buildGynecologyChartData = (
    userData,
  ) => {
    if (!userData?.length) return [];

    const anchorRecord = [...userData].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    )[0];

    const anchorWeek = anchorRecord.week;
    const anchorDate = dayjs(anchorRecord.createdAt);

    const projectionMap = {};

    systolicProjection.forEach((sys, index) => {
      const dia = diastolicProjection[index];

      const diffWeeks = sys.week - anchorWeek;

      const projectionDate = anchorDate
        .add(diffWeeks * 7, "day")
        .startOf("day");

      projectionMap[projectionDate.format("YYYY-MM-DD")] = {
        fullDate: projectionDate.valueOf(),
        sysRange: [sys.min, sys.max],
        diaRange: [dia.min, dia.max],
        week: sys.week,
      };
    });

    userData.forEach((record) => {
      const key = dayjs(record.createdAt).format("YYYY-MM-DD");

      if (projectionMap[key]) {
        projectionMap[key].Systolic = record.systolic;
        projectionMap[key].Diastolic = record.diastolic;
      } else {
        projectionMap[key] = {
          fullDate: dayjs(record.createdAt).valueOf(),
          Systolic: record.systolic,
          Diastolic: record.diastolic,
        };
      }
    });

    return Object.values(projectionMap).sort(
      (a, b) => a.fullDate - b.fullDate
    );
  };
  const mergeBpRanges = (userData, systolicBp, diastolicBp) => {
    const firstProjectionWeek = systolicBp?.[0]?.week;

    const normalizedUserData = userData.map((entry, index) => {
      if (index === 0 && entry.week < 5) {
        return {
          ...entry,
          week: firstProjectionWeek,
        };
      }
      return entry;
    });

    const merged = [];

    for (let i = 0; i < systolicBp.length; i++) {
      const week = systolicBp[i].week;

      const user =
        normalizedUserData.find(
          (entry) => Number(entry.week) === Number(week)
        ) || {};

      merged.push({
        week,
        sysRange: [systolicBp[i].min, systolicBp[i].max],
        diaRange: [diastolicBp[i].min, diastolicBp[i].max],
        ...(user.systolic !== undefined && {
          Systolic: user.systolic,
        }),
        ...(user.diastolic !== undefined && {
          Diastolic: user.diastolic,
        }),
      });
    }

    return merged;
  };

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isXLUp = useMediaQuery(theme.breakpoints.up("xl"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const mergedData = mergeBpRanges(data, systolicProjection, diastolicProjection)

  const chartData =
    patientType === "GYNECOLOGY"
      ? data?.length
        ? buildGynecologyChartData(data)
        : buildProjectionOnlyData()
      : mergeBpRanges(data, systolicProjection, diastolicProjection);

  const firstDate = data?.length
    ? dayjs(data[0].createdAt).subtract(3, "day").valueOf()
    : undefined

  const lastDate = data?.length
    ? dayjs(data[data.length - 1].createdAt).add(3, "day").valueOf()
    : undefined;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const validData = payload.filter(
      (entry) =>
        entry.name !== "sysRange" &&
        entry.name !== "diaRange" &&
        entry.value !== undefined &&
        entry.value !== null
    );

    if (validData.length === 0) return null;

    return (
      <div
        style={{
          background: "#fff",
          padding: "6px 10px",
          borderRadius: "6px",
          border: "1px solid #ccc",
          fontSize: "12px",
        }}
      >
        {patientType === "GYNECOLOGY" && (
          <div
            style={{
              fontWeight: 700,
              marginBottom: "4px",
            }}
          >
            {dayjs(label).format("DD MMM YYYY")}
          </div>
        )}

        {validData.map((entry, index) => (
          <div key={index} style={{ fontWeight: 600 }}>
            {entry.name}: {entry.value} mmHg
          </div>
        ))}
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height="100%" >
      <ComposedChart data={chartData} margin={{ top: 2, right: isXLUp ? 30 : isLgUp ? 15 : 15, left: isXLUp ? 5 : isLgUp ? 4 : 4, bottom: 25 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={true} />
        {patientType === "GYNECOLOGY" ? (
          <XAxis
            dataKey="fullDate"
            type="number"
            scale="time"
            domain={
              data?.length
                ? [firstDate, lastDate]
                : ["dataMin", "dataMax"]
            }
            tickFormatter={(value) =>
              dayjs(value).format("DD MMM")
            }
            tick={{
              fill: "#000",
              fontSize: "7.5px",
              fontWeight: "bold",
            }}
          />
        ) : <XAxis dataKey="week" label={{ value: "Week", position:  "insideBottomRight", dy: 10, dx: -5, fontWeight: 'bold', fontSize: '10px', fill: "#000", }} tick={{ fill: "#000", fontSize: '8px', fontWeight: 'bold' }} />}
        <YAxis label={{
          value: "BP (mmHg)", angle: -90,     position: "insideLeft",
          dx: 15,
       fontWeight: 'bold', fontSize: '10px', style: {
            textAnchor: "middle", fill: "#000",
          },
        }} axisLine={false} tickLine={false} tick={{ fill: "#000", fontSize: '8px', fontWeight: 'bold' }} domain={[50, 160]} />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          verticalAlign="top"
          align="center"
          iconType="circle"
          iconSize={6}
          wrapperStyle={{
            paddingBottom: '15px',
            fontSize: '12px',
            color: 'rgba(0, 0, 0, 1)',
          }}
          formatter={(value) => {
            let color = "rgba(0, 0, 0, 1)";
            return <span style={{ color }}>{value}</span>;
          }}
        />

        <Area
          dataKey="sysRange"
          fill="#E6EAEA"
          stroke="#E6EAEA"
          legendType="none"
        />

        <Area
          dataKey="diaRange"
          fill="#E6EAEA"
          stroke="#E6EAEA"
          legendType="none"
          connectNulls
        />

        <Line type="monotone" dataKey="Systolic" stroke="#6CE5E8" strokeWidth={1} dot fill="#6CE5E8"
          fillOpacity={0.4} connectNulls />
        <Line type="monotone" dataKey="Diastolic" stroke="#0097B2" strokeWidth={1} dot fill="#0097B2"
          fillOpacity={0.4} connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
